/**
 * Le cinque schede del telefono, e quale si accende dove.
 *
 * ## Perché sta nel dominio e non nella barra
 *
 * Perché è una **regola**, non un disegno: dice in quale ramo dell'applicazione ci si trova, e la
 * risposta non è leggibile dal nome della schermata. L'aggregatore accende Home, la scheda di un
 * titolo accende On demand, il lettore accende Guida **o** On demand a seconda di cosa sta
 * riproducendo (`docs/04-SCHERMATE-MOBILE.md` §2).
 *
 * Scritta dentro la barra, l'unico modo di verificarla sarebbe montare un componente e guardare un
 * colore. Qui si prova come quel che è: una funzione da un posto a una scheda.
 *
 * ## Le schede non sono le sezioni della TV
 *
 * Il televisore ha **otto** voci di menu, il telefono **cinque** schede, e non è un sottoinsieme:
 *
 * - `movies` e `series` sono due voci sulla TV e **una sola** scheda qui, perché il telefono mette
 *   `Film` e `Serie TV` come due etichette dentro On demand.
 * - `favorites` sulla TV è una schermata; sul telefono **non esiste**, perché i preferiti si fanno
 *   con la stella accanto a ogni canale — cioè dentro la Guida, che è dove quella stella sta.
 * - `pin` non è una voce nemmeno sulla TV: ci si arriva dalle impostazioni, ed è lì che resta.
 *
 * Chi guarda la barra deve sapere sempre in quale ramo si trova. Una schermata che non accende
 * nessuna scheda è peggio di una che ne accende una discutibile: lascia la barra spenta, e chi legge
 * non sa più dov'è.
 */

/** Le cinque schede, nell'ordine in cui compaiono in basso. */
export const PHONE_TABS = ['home', 'guide', 'ondemand', 'search', 'options'] as const;

export type PhoneTab = (typeof PHONE_TABS)[number];

/**
 * Dove si trova l'applicazione, dal punto di vista della barra.
 *
 * Non è lo stato di navigazione — quello è più ricco e vive negli host. È **quel che serve a
 * decidere la scheda**, e nient'altro: aggiungerci campi significherebbe dover aggiornare questa
 * regola ogni volta che cambia qualcosa che non la riguarda.
 */
export type PhonePlace =
  /** Una delle sezioni che sulla TV stanno nel menu laterale. */
  | { readonly kind: 'section'; readonly section: string }
  /** L'aggregatore di una fascia della home: «tutti gli eventi di stasera». */
  | { readonly kind: 'aggregator' }
  /** La scheda di un film o di una serie, l'elenco dei generi, un genere. */
  | { readonly kind: 'vod' }
  /**
   * Il lettore.
   *
   * `live` o `vod`: è **l'unico posto in cui la stessa schermata accende schede diverse**, e dipende
   * da cosa si sta guardando. Chi esce dal lettore torna da dove è venuto, e la barra deve averlo
   * detto per tutto il tempo.
   */
  | { readonly kind: 'player'; readonly playing: 'live' | 'vod' };

/**
 * Le sezioni della TV che hanno una scheda sul telefono.
 *
 * Elenco esplicito e non una regola generale: le due mappe non sono parallele, e un `default`
 * furbo — «se non so, Home» — nasconderebbe esattamente il caso che va deciso a mano. Una sezione
 * nuova deve comparire qui, non scivolare in silenzio su Home.
 */
const TAB_OF_SECTION: Readonly<Record<string, PhoneTab>> = {
  home: 'home',
  // La Guida, che sulla TV si chiama `live`.
  live: 'guide',
  search: 'search',
  // Due voci sulla TV, una scheda qui: dentro On demand sono due etichette.
  movies: 'ondemand',
  series: 'ondemand',
  /*
    I preferiti non hanno una scheda, e la Guida è la risposta giusta.

    Sul telefono non esiste una schermata dei preferiti: si aggiungono e si tolgono con la stella
    accanto a ogni canale, cioè **dentro la Guida**. Se un giorno qualcuno aprisse quella schermata
    anche qui, la scheda accesa sarebbe quella del posto da cui quella stella si tocca.
  */
  favorites: 'guide',
  settings: 'options',
  // Ci si arriva dalle impostazioni, ed è lì che la barra deve continuare a indicare.
  pin: 'options',
};

/**
 * Quale scheda accendere, dato dove si è.
 *
 * Risponde `null` **solo** per una sezione che non conosce. Non è un caso teorico: è il giorno in
 * cui qualcuno aggiunge una voce al menu della TV e si dimentica del telefono, e il modo in cui si
 * manifesta è una barra con tutte le schede spente — visibile subito, invece di una scheda sbagliata
 * accesa con sicurezza.
 */
export function tabFor(place: PhonePlace): PhoneTab | null {
  switch (place.kind) {
    case 'section':
      return TAB_OF_SECTION[place.section] ?? null;
    case 'aggregator':
      // Un aggregatore si apre **da una fascia della home**, e indietro torna lì.
      return 'home';
    case 'vod':
      // Scheda di un titolo, elenco dei generi, un genere: tutto il ramo On demand.
      return 'ondemand';
    case 'player':
      return place.playing === 'live' ? 'guide' : 'ondemand';
  }
}

/**
 * Dove si va toccando una scheda.
 *
 * ## Il verso inverso non è una tabella rovesciata
 *
 * `tabFor` è **molti a uno**: `movies` e `series` finiscono entrambe su `ondemand`, `favorites` sulla
 * Guida, `pin` su Opzioni. Rovesciandola si otterrebbe una scelta ambigua proprio dove serve una
 * risposta sola — e la risposta non è «una qualsiasi delle due».
 *
 * ## `ondemand` porta ai film, e va detto
 *
 * Non perché i film contino più delle serie: perché **una delle due deve essere la prima**, e sulla
 * schermata On demand `Film` è l'etichetta a sinistra. Portare alle serie farebbe aprire la scheda
 * con la seconda etichetta attiva, cioè con la gerarchia visiva che dice «stai guardando le serie»
 * mentre l'ordine delle etichette dice il contrario.
 *
 * Chi stava già in On demand e aveva scelto `Serie TV` **non deve** tornare ai film: quello non è un
 * ritorno a questa funzione, è lo stato della schermata, e lo tiene chi la monta.
 *
 * ## I preferiti non sono qui
 *
 * `favorites` è una destinazione del televisore. Sul telefono non esiste come schermata — la stella
 * sta accanto a ogni canale — quindi toccando `guide` si va alla Guida e basta: tornarci da
 * `favorites` sarebbe una schermata che il telefono non ha.
 */
export function sectionForTab(tab: PhoneTab): string {
  switch (tab) {
    case 'home':
      return 'home';
    case 'guide':
      // Sul televisore la Guida si chiama `live`.
      return 'live';
    case 'ondemand':
      return 'movies';
    case 'search':
      return 'search';
    case 'options':
      return 'settings';
  }
}
